import React, { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { ButtonProps, ButtonVariantMap } from './types'
import Tooltip, { TooltipTrigger } from '../Tooltip'

const sizes = {
  sm: { width: '70px', height: '24px', font: '12px' },
  md: { width: '76px', height: '32px', font: '14px' },
  bg: { width: '82px', height: '40px', font: '16px' },
}

const containedButtonStyle = css<ButtonProps>`
  min-width: ${({ size }) => sizes[size].width};
  min-height: ${({ size }) => sizes[size].height};
  ${({ fullWidth }) => fullWidth && 'width: 100%'};
  padding: 7px 15px;

  background-color: ${({ color }) => (!!color ? `var(--${color}-500)` : 'var(--gray-0)')};

  border: 1px solid ${({ color }) => (!!color ? `var(--${color}-500)` : 'var(--gray-400)')};
  border-radius: 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-family: 'Fira Sans';
  color: ${({ color }) => (!!color ? 'var(--gray-0)' : 'var(--gray-600)')};
  font-size: ${({ size }) => sizes[size].font};
  font-weight: 500;
  cursor: pointer;

  ${({ disabled, ignorePointerEventsImportant }) =>
    disabled
      ? css`
          background-color: var(--blueish-50) !important;
          border-color: var(--blueish-200) !important;
          color: var(--blueish-400) !important;
          pointer-events: none !important;
          ${ignorePointerEventsImportant &&
          css`
            pointer-events: all !important;
          `}
        `
      : css`
          &:hover {
            opacity: 0.8;
          }
        `}
`

const outlinedButtonStyle = css<ButtonProps>`
  ${containedButtonStyle}
  background-color: ${({ color, isTransparent }) =>
    isTransparent ? 'transparent' : !!color ? `var(--${color}-100)` : 'transparent'};
  color: ${({ color }) => (!!color ? `var(--${color}-500)` : 'var(--gray-600)')};
  border-color: ${({ color }) => (!!color ? `var(--${color}-500)` : 'var(--blueish-400)')};
`

const linkButtonStyle = css<ButtonProps>`
  font-weight: 500;
  border: none;
  background: transparent;
  color: ${({ color }) => color && `var(--${color}-500)`};
  padding: 0;
  cursor: pointer;

  ${({ disabled, ignorePointerEventsImportant }) =>
    disabled
      ? css`
          text-decoration: none;
          pointer-events: none !important;
          color: var(--gray-300);
          ${ignorePointerEventsImportant &&
          css`
            pointer-events: all !important;
          `}
        `
      : css`
          &:hover {
            text-decoration: underline;
          }
        `}
`

const buttonVariants: ButtonVariantMap = {
  contained: containedButtonStyle,
  link: linkButtonStyle,
  outlined: outlinedButtonStyle,
}

const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${({ variant }) => buttonVariants[variant]}
  transition: background-color 0.1s, border 0.1s;
  svg,
  .start-icon {
    margin-right: 4px;
  }

  .end-icon {
    margin-left: 4px;
  }

  ${({ gutterBottom }) =>
    gutterBottom &&
    css`
      margin-bottom: 16px;
    `}
`

Button.defaultProps = {
  variant: 'contained',
  size: 'md',
}

export default Button

export const ButtonWithTooltip: React.FC<ButtonProps & { tooltip?: ReactNode; offsetValue?: number }> = ({
  tooltip,
  offsetValue,
  ...buttonProps
}) => {
  if (!tooltip) return <Button {...buttonProps} />
  return (
    <Tooltip hasArrow placement="top" offsetValue={offsetValue || 12} description={tooltip}>
      <TooltipTrigger>
        <Button {...buttonProps} />
      </TooltipTrigger>
    </Tooltip>
  )
}
